import { configureSync, getConfig, getConsoleSink, withFilter } from '@logtape/logtape'
import { getOpenTelemetrySink } from '@logtape/otel'

import { createBrowserLoggerProvider } from '../shared/libs/logger/browser-otel-logger'
import { initBrowserTracing } from '../shared/libs/logger/browser-otel-tracer'
import { registerBrowserErrorHandlers } from '../shared/libs/logger/client-error-capture'

const isProduction = process.env.NEXT_PUBLIC_APP_ENV === 'production'
const browserLoggerLevel = isProduction ? 'warning' : 'info'

// 브라우저 로그/트레이스를 same-origin 프록시(/api/telemetry)를 거쳐 사이드카 OTel Collector → Loki/Tempo로 보낸다.
const isOtelLogsEnabled = process.env.NEXT_PUBLIC_OTEL_LOGS_ENABLED === 'true'
const isOtelTracesEnabled = process.env.NEXT_PUBLIC_OTEL_TRACES_ENABLED === 'true'
const appEnv = process.env.NEXT_PUBLIC_APP_ENV ?? 'local'

// service.name을 인프라/백엔드(`podo-web-{dev|stage|qa|prod}`)와 동일하게 정규화한다.
// 매핑되지 않는 환경(local 등)은 텔레메트리를 전송하지 않는다.
const APP_ENV_TO_SERVICE_SUFFIX: Record<string, string | undefined> = {
  development: 'dev',
  qa: 'qa',
  staging: 'stage',
  production: 'prod',
}
const serviceSuffix = APP_ENV_TO_SERVICE_SUFFIX[appEnv]
const otelService = serviceSuffix ? { name: `podo-web-${serviceSuffix}`, environment: serviceSuffix } : null

const OTEL_LOGS_PROXY_URL = '/api/telemetry/v1/logs'
const OTEL_TRACES_PROXY_URL = '/api/telemetry/v1/traces'
const OTEL_TRACES_SAMPLE_RATIO = isProduction ? 0.1 : 1
// 텔레메트리 전송 요청 자체와 노이즈성 요청은 트레이싱에서 제외한다.
const OTEL_TRACE_IGNORE_URLS = [/\/api\/telemetry/, /\/api\/vitals/, /\/api\/analytics/]
let isTracingInitialized = false

// 브라우저가 직접 호출하는 cross-origin 백엔드(NEXT_PUBLIC_PODO_API_URL, 예: 결제)에
// traceparent를 주입할 origin 패턴. same-origin은 기본 주입되므로 BFF 경유 경로는 불필요.
function buildBackendPropagationUrls(): RegExp[] {
  const podoApiUrl = process.env.NEXT_PUBLIC_PODO_API_URL
  if (!podoApiUrl) {
    return []
  }
  try {
    const origin = new URL(podoApiUrl).origin
    return [new RegExp(`^${origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`)]
  } catch {
    return []
  }
}
const OTEL_TRACE_PROPAGATE_CORS_URLS = buildBackendPropagationUrls()

export function instrumentClient() {
  if (otelService && isOtelTracesEnabled && !isTracingInitialized) {
    isTracingInitialized = true
    initBrowserTracing({
      serviceName: otelService.name,
      environment: otelService.environment,
      otlpUrl: OTEL_TRACES_PROXY_URL,
      sampleRatio: OTEL_TRACES_SAMPLE_RATIO,
      ignoreUrls: OTEL_TRACE_IGNORE_URLS,
      propagateTraceHeaderCorsUrls: OTEL_TRACE_PROPAGATE_CORS_URLS,
    })
  }

  if (getConfig()) {
    return
  }

  const otelSink =
    otelService && isOtelLogsEnabled
      ? withFilter(
          getOpenTelemetrySink({
            loggerProvider: createBrowserLoggerProvider({
              serviceName: otelService.name,
              environment: otelService.environment,
              otlpUrl: OTEL_LOGS_PROXY_URL,
            }),
          }),
          browserLoggerLevel,
        )
      : null

  const appSinks = ['console', ...(otelSink ? ['otel'] : [])]

  configureSync({
    sinks: {
      console: getConsoleSink(),
      ...(otelSink ? { otel: otelSink } : {}),
    },
    loggers: [
      {
        category: ['web'],
        sinks: appSinks,
        lowestLevel: browserLoggerLevel,
      },
      { category: ['logtape', 'meta'], sinks: ['console'], lowestLevel: 'warning' },
    ],
  })

  // logtape 설정 후 전역 에러 핸들러(uncaught error/unhandled rejection)를 등록한다.
  registerBrowserErrorHandlers()
}
